/*
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
 */
import { computed, unref } from "vue";
import { useConfigStore } from "@/stores/config.js";

/**
 * Build the parameters passed to pdf.js getDocument (via usePDF).
 *
 * Since pdf.js 5.x, JPEG2000 (and JBIG2 / ICC color) images are decoded by
 * WASM modules (openjpeg.wasm, qcms_bg.wasm) that pdf.js fetches at runtime
 * from `wasmUrl`. When wasmUrl is left undefined it defaults to null, so the
 * worker tries to fetch "nullopenjpeg.wasm", fails, and JPEG2000 images are
 * not rendered (the rest of the page still renders). We therefore point
 * wasmUrl at "<BASE_URL>pdfjs/wasm/", where vite-plugin-static-copy serves the
 * pdfjs-dist/wasm/* files in the consuming app.
 *
 * BASE_URL is the consuming app's Vite base (e.g. "/media/"). It is read from
 * the ConfigStore at runtime because this library is consumed as a pre-built
 * bundle, so import.meta.env.BASE_URL would be frozen to the library's own
 * base instead of the app's.
 *
 * @param {string|object} src - A PDF URL or a DocumentInitParameters object.
 * @returns {object} DocumentInitParameters including wasmUrl.
 */
export function buildPdfSource(src) {
  const configStore = useConfigStore();
  const base = configStore.BASE_URL || "/";
  const params = typeof src === "string" ? { url: src } : { ...src };
  params.wasmUrl = `${base}pdfjs/wasm/`;
  return params;
}

/**
 * Reactive variant of buildPdfSource for callsites that pass a ref to usePDF
 * (so pdf.js reloads when the URL changes). Returns a computed that yields the
 * enriched parameters when the source has a value, or null otherwise so that
 * usePDF skips loading.
 *
 * @param {import('vue').Ref<string|object|null>} srcRef - Reactive PDF source.
 * @returns {import('vue').ComputedRef<object|null>}
 */
export function buildPdfSourceRef(srcRef) {
  return computed(() => {
    const value = unref(srcRef);
    return value ? buildPdfSource(value) : null;
  });
}
